"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { useAtomValue } from "jotai";
import React, { type ReactElement, useState } from "react";
import { ObservatoryCard } from "@/components";
import { featuredObservatoriesAtom } from "@/store/home";
import {
  cn,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@oreme/ui";

const featureObservatoryCardListItemVariants = cva(
  "absolute transform transition-transform",
  {
    variants: {
      position: {
        center: "left-1/2 -translate-x-1/2 -translate-y-4",
        left: "left-0 rotate-12 -translate-x-8",
        right: "right-0 -rotate-12 translate-x-8",
      },
      hovered: {
        true: "scale-110",
        false: "scale-100",
      },
    },
    defaultVariants: {
      position: "center",
      hovered: false,
    },
  },
);

interface FeatureObservatoryCardListItem extends VariantProps<
  typeof featureObservatoryCardListItemVariants
> {
  id: number;
}

interface HomeFeaturedDevicesListProps {
  type?: string;
}

function HomeFeaturedDevicesList({
  type = "carousel",
}: Readonly<HomeFeaturedDevicesListProps>): ReactElement | null {
  const featuredObservatories = useAtomValue(featuredObservatoriesAtom);
  const [hoverStack, setHoverStack] = useState<number[]>([]);

  let cards: FeatureObservatoryCardListItem[];
  const count = featuredObservatories.length;

  if (count >= 3) {
    cards = [
      {
        id: 0,
        position: "center",
      },
      {
        id: 1,
        position: "left",
      },
      {
        id: 2,
        position: "right",
      },
    ];
  } else if (count === 2) {
    cards = [
      {
        id: 0,
        position: "left",
      },
      {
        id: 1,
        position: "right",
      },
    ];
  } else if (count === 1) {
    cards = [
      {
        id: 0,
        position: "center",
      },
    ];
  } else {
    return null;
  }

  const getZIndex = (cardId: number) => {
    const index = hoverStack.indexOf(cardId);
    if (index === -1) {
      // Not in hover stack - use default z-index
      const card = cards.find((c) => c.id === cardId);
      return card?.position === "center" ? 10 : 0;
    }
    // In hover stack - z-index based on position (20+)
    return 20 + index;
  };

  const isHovered = (cardId: number) => {
    return hoverStack.length > 0 && hoverStack.at(-1) === cardId;
  };

  const handleMouseEnter = (cardId: number) => {
    setHoverStack((prev) => {
      // Remove cardId if it exists and add it to the end
      const filtered = prev.filter((id) => id !== cardId);
      return [...filtered, cardId];
    });
  };

  if (type === "carousel") {
    return (
      <Carousel
        className="relative"
        opts={{
          align: "start", // Align slides to the start
          containScroll: "trimSnaps", // Prevent empty space at the end
        }}
      >
        <CarouselContent className="pl-4">
          {featuredObservatories.map((observatory) => (
            <CarouselItem key={observatory.id} className="basis-auto py-16">
              <ObservatoryCard
                observatory={observatory}
                className="w-64 min-h-100"
              />
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className="bottom-2 top-auto left-auto right-12" />
        <CarouselNext className="bottom-2 top-auto left-auto right-4" />
      </Carousel>
    );
  }

  return (
    <ul className="relative min-h-95 p-0 m-0 list-none">
      {cards.map(({ id, position }) => (
        <li
          key={id}
          onMouseEnter={() => handleMouseEnter(id)}
          style={{ zIndex: getZIndex(id) }}
          className={cn(
            featureObservatoryCardListItemVariants({
              position: position,
              hovered: isHovered(id),
            }),
          )}
        >
          <ObservatoryCard
            observatory={featuredObservatories[id]}
            className="w-64 min-h-100"
          />
        </li>
      ))}
    </ul>
  );
}

export { HomeFeaturedDevicesList };
